import React, { useState, useEffect, useDeferredValue } from "react";
import api from "../api/api";
import { exportSectionsToCsv } from "../utils/exportToExcel";
import { formatDateDDMMYYYY } from "../utils/dateFormat";
import { getTotalsByColumn, isTotalsTable } from "../utils/tableTotals";

const BoEData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Summary Rev");
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [rowHeight, setRowHeight] = useState("compact");
  const [fullscreenTable, setFullscreenTable] = useState(null);

  const tabs = [
    { id: "Summary Rev", label: "Summary Rev" },
    { id: "Sales by MCNo", label: "Sales by BOE No" },
    { id: "Scrap BY BOE", label: "Scrap BY BOE" },
    { id: "WIP By BOE", label: "WIP By BOE" },
    { id: "MOOWR Purchase", label: "MOOWR Purchase" },
  ];

  useEffect(() => {
    fetchData(activeTab);
    setSearchQuery(""); // Clear search when tab changes
  }, [activeTab]);

  const fetchData = async (type) => {
    setLoading(true);
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetBoEDatas(userName, type);
      const boEData = response?.BoEData || response;
      setData(boEData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportQtyKgsClick = async (boeNo) => {
    setPopupLoading(true);
    setShowPopup(true);
    setPopupType("import");
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetImpQtyKgsByBOENo(userName, boeNo);
      setPopupData(response);
    } catch (err) {
      console.error("Error fetching Import Qty Kgs data:", err);
      setPopupData(null);
    } finally {
      setPopupLoading(false);
    }
  };

  const handleTotalShippedMOOWRClick = async (boeNo) => {
    setPopupLoading(true);
    setShowPopup(true);
    setPopupType("moowr");
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetSalesShippedunderMOOWR(userName, boeNo);
      setPopupData(response);
    } catch (err) {
      console.error("Error fetching Total Shipped MOOWR data:", err);
      setPopupData(null);
    } finally {
      setPopupLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
    setPopupType(null);
  };

  const handleExportToExcel = () => {
    if (!data) return;

    const addSection = (sections, tableRows, title) => {
      const filteredRows = filterTableData(tableRows);
      if (!filteredRows || filteredRows.length === 0) return;
      const columns = Object.keys(filteredRows[0] || {});
      if (!columns.length) return;

      const headers = columns.map((col) => col.replace(/_/g, " "));
      const records = filteredRows.map((row) =>
        columns.map((col) => {
          const value = row[col];
          if (col.toLowerCase().includes("date")) return formatDateDDMMYYYY(value);
          if (value === null || value === undefined) return "-";
          return value;
        })
      );

      sections.push({ title, headers, records });
    };

    const sections = [];
    addSection(sections, data.Table, activeTab);
    addSection(sections, data.Table1, "Additional Details");
    addSection(sections, data.Table2, "Related Records");
    addSection(sections, data.Table3, "More Information");
    addSection(sections, data.Table4, "Extra Data");

    exportSectionsToCsv({
      sections,
      fileNamePrefix: (activeTab || "BoE_Data").replace(/\s+/g, "_"),
    });
  };

  const formatDate = (dateString) => {
    return formatDateDDMMYYYY(dateString);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") return value.toLocaleString();
    return value;
  };

  const isNumericValue = (value) => {
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value !== "string") return false;
    const normalized = value.trim().replace(/,/g, "");
    return /^-?\d+(\.\d+)?$/.test(normalized);
  };

  const isColumnConfigTable = (table) => {
    return (
      Array.isArray(table) &&
      table.length > 0 &&
      ["DbColDisplay", "DisplayColName", "Visible", "Priority"].every((key) => key in table[0])
    );
  };

  const getOrderedColumns = (tableData, columnConfig) => {
    const defaultColumns = Object.keys(tableData[0] || {});
    if (!columnConfig) return defaultColumns;

    const configuredColumns = new Set(
      columnConfig
        .filter((cfg) => cfg.DbColDisplay)
        .map((cfg) => cfg.DbColDisplay)
    );

    const configColumns = columnConfig
      .filter((cfg) => Number(cfg.Visible) !== 0 && cfg.DbColDisplay)
      .sort((a, b) => Number(a.Priority || 9999) - Number(b.Priority || 9999))
      .map((cfg) => cfg.DbColDisplay)
      .filter((col) => defaultColumns.includes(col));

    const remainingColumns = defaultColumns.filter((col) => !configuredColumns.has(col));
    return [...configColumns, ...remainingColumns];
  };

  const getDisplayNameMap = (columnConfig) => {
    if (!columnConfig) return {};
    return columnConfig.reduce((acc, cfg) => {
      if (cfg.DbColDisplay) acc[cfg.DbColDisplay] = cfg.DisplayColName || cfg.DbColDisplay;
      return acc;
    }, {});
  };

  const filterTableData = (tableData) => {
    if (!tableData || !deferredSearchQuery) return tableData;
    const normalizedSearch = deferredSearchQuery.toLowerCase();

    return tableData.filter((row) =>
      Object.values(row).some((value) =>
        value !== null && value !== undefined &&
        String(value).toLowerCase().includes(normalizedSearch)
      )
    );
  };

  const renderRecordAsCard = (record) => {
    if (!record) return null;

    const entries = Object.entries(record);

    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow p-1.5 border border-blue-100 mb-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1.5">
          {entries.map(([key, value]) => {
            let displayValue = value;
            
            if (value === null || value === undefined) {
              displayValue = "-";
            } else if (typeof value === "number") {
              displayValue = value.toLocaleString();
            } else if (key.toLowerCase().includes("date")) {
              displayValue = formatDateDDMMYYYY(value);
            }

            return (
              <div key={key} className="flex items-center">
                <span className="text-xs font-bold text-gray-700 mr-1 min-w-fit">
                  {key.replace(/_/g, " ")}:
                </span>
                <span className={`text-xs text-gray-800 break-words ${typeof value === 'number' ? 'text-right' : ''}`}>
                  {displayValue}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTable = (tableData, title, tableKey = null, isFullscreen = false, totalsTable = null, columnConfig = null) => {
    if (!tableData || tableData.length === 0) {
      return (
        <div className="bg-white rounded-2xl shadow p-4">
          <h2 className="text-lg font-semibold mb-2">{title}</h2>
          <p className="text-gray-400">No data available</p>
        </div>
      );
    }

    const columns = getOrderedColumns(tableData, columnConfig);
    const displayNameMap = getDisplayNameMap(columnConfig);
    const cellPaddingStyle = rowHeight === "compact" 
      ? { paddingLeft: "6px", paddingRight: "6px", paddingTop: "3px", paddingBottom: "3px" }
      : { paddingLeft: "16px", paddingRight: "16px", paddingTop: "8px", paddingBottom: "8px" };
    const headerCellStyle = {
      paddingLeft: "8px",
      paddingRight: "8px",
      paddingTop: "10px",
      paddingBottom: "10px",
      minHeight: "52px",
      lineHeight: "1.2",
    };
    const numericColumns = new Set(
      columns.filter((col) => tableData.some((row) => {
        const value = row[col];
        return typeof value === "number" || isNumericValue(value);
      }))
    );
    const totalsByColumn = getTotalsByColumn(totalsTable, columns);

    return (
      <div className="bg-white rounded-2xl shadow">
        {/* Card Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {tableData.length} records
            </span>
            <button
              onClick={() => setFullscreenTable(fullscreenTable === tableKey ? null : tableKey)}
              className="p-2 hover:bg-gray-100 rounded transition-colors text-gray-600 hover:text-gray-900"
              title={fullscreenTable === tableKey ? "Shrink" : "Expand"}
            >
              {fullscreenTable === tableKey ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7m0 0l-7 7m7-7H6" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5v4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 0h-4m4 0l-5-5" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className={`overflow-auto ${isFullscreen ? "max-h-[calc(100vh-56px)]" : "max-h-[500px]"}`} style={{ borderSpacing: "0" }}>
          <table className="min-w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-gray-100 z-10">
              <tr>
                {columns.map((col) => {
                  const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                  const isBoeSlNo = displayLabel.replace(/[_\s]/g, "").toLowerCase() === "boeslno";
                  const totalValue = totalsByColumn[col];
                  return (
                    <th
                      key={col}
                      className={`font-bold text-gray-700 whitespace-normal break-words align-middle border-b ${
                        isBoeSlNo ? "text-center" : ""
                      }`}
                      style={headerCellStyle}
                    >
                      <div className="flex flex-col">
                        <span className="leading-tight">{displayLabel}</span>
                        <span
                          className={`text-sm font-medium leading-tight min-h-[18px] ${
                            totalValue !== undefined ? "text-emerald-700" : "text-transparent"
                          }`}
                        >
                          {totalValue !== undefined ? totalValue : "\u00A0"}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {tableData.map((row, i) => (
                <tr
                  key={i}
                  onClick={() => setSelectedRowIndex(selectedRowIndex === i ? null : i)}
                  className={`border-b last:border-none cursor-pointer transition-colors ${
                    selectedRowIndex === i
                      ? "bg-blue-300 hover:bg-blue-400"
                      : i % 2 === 0
                      ? "bg-gray-50 hover:bg-gray-100"
                      : "bg-white hover:bg-blue-50"
                  }`}
                  style={{ height: rowHeight === "compact" ? "auto" : "auto" }}
                >
                  {columns.map((col) => {
                    let value = row[col];
                    const wasNumeric = numericColumns.has(col);
                    const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                    const isBoeSlNo = displayLabel.replace(/[_\s]/g, "").toLowerCase() === "boeslno";

                    // Make Import Qty Kgs column clickable
                    if (col === "Import Qty Kgs" || col === "Import_Qty_Kgs") {
                      return (
                        <td
                          key={col}
                          className="whitespace-nowrap text-blue-600 cursor-pointer hover:text-blue-800 hover:underline font-semibold text-right"
                          style={cellPaddingStyle}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImportQtyKgsClick(row.Rev_BOE || row.BoeNo || row.boeNo);
                          }}
                        >
                          {formatNumber(value)}
                        </td>
                      );
                    }

                    if (col === "Total Shipped MOOWR" || col === "Total_Shipped_MOOWR") {
                      return (
                        <td
                          key={col}
                          className="whitespace-nowrap text-green-600 cursor-pointer hover:text-green-800 hover:underline font-semibold text-right"
                          style={cellPaddingStyle}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTotalShippedMOOWRClick(row.Rev_BOE || row.BoeNo || row.boeNo);
                          }}
                        >
                          {formatNumber(value)}
                        </td>
                      );
                    }

                    if (col.toLowerCase().includes("date")) {
                      value = formatDate(value);
                    } else if (typeof value === "number") {
                      value = formatNumber(value);
                    } else if (value === null) {
                      value = "-";
                    }

                    return (
                      <td
                        key={col}
                        className={`whitespace-nowrap ${
                          selectedRowIndex === i ? "font-semibold text-gray-900" : "text-gray-700"
                        } ${isBoeSlNo ? "text-center" : wasNumeric ? "text-right" : "text-left"}`}
                        style={cellPaddingStyle}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-2 pl-2 space-y-2 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white p-5 rounded-2xl shadow flex items-center justify-between">
        <div className="flex-1 text-center">
          <h1 className="text-2xl font-bold">BoE Dashboard</h1>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {new Date().toLocaleString()}
        </span>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow p-2">
        <div className="flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar and Controls */}
      <div className="bg-white p-2 rounded-2xl shadow space-y-2">
        <input
          type="text"
          placeholder="Search records..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
        
        {/* Row Height Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Row Height:</span>
          <button
            onClick={() => setRowHeight(rowHeight === "compact" ? "normal" : "compact")}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              rowHeight === "compact"
                ? "bg-blue-500 hover:bg-blue-600 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Toggle Row Height"
          >
            {rowHeight === "compact" ? "Compact" : "Normal"}
          </button>
          <button
            onClick={handleExportToExcel}
            className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-100 hover:bg-emerald-200 text-emerald-800 transition-colors"
            title="Export to Excel"
          >
            Export
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
        </div>
      )}

      {/* Data Table */}
      {!loading && data && (
        <div className="space-y-3">
          {(() => {
            const mainConfig = isColumnConfigTable(data.Table1) ? data.Table1 : null;
            const mainColumns = data.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
            const mainTotals = isTotalsTable(data.Table2, mainColumns) ? data.Table2 : null;
            return (
              <>
          {data.Table && renderTable(filterTableData(data.Table), activeTab, "table1", false, mainTotals, mainConfig)}
          {data.Table1 && !isColumnConfigTable(data.Table1) && renderTable(filterTableData(data.Table1), "Additional Details", "table2")}
          {data.Table2 && !mainTotals && renderTable(filterTableData(data.Table2), "Related Records", "table3")}
          {data.Table3 && renderTable(filterTableData(data.Table3), "More Information", "table4")}
          {data.Table4 && renderTable(filterTableData(data.Table4), "Extra Data", "table5")}
              </>
            );
          })()}
        </div>
      )}

      {/* No Data State */}
      {!loading && !data && (
        <div className="bg-white rounded-2xl shadow p-8 text-center">
          <p className="text-gray-500">No data available for this tab</p>
        </div>
      )}

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closePopup}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">
                {popupType === "import" ? "Import Qty Kgs Details" : "Total Shipped MOOWR Details"}
              </h2>
              <button 
                onClick={closePopup}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-auto max-h-[calc(90vh-120px)]">
              {popupLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
                </div>
              ) : popupData ? (
                <div className="space-y-4">
                  {popupType === "import" ? (
                    <>
                      {popupData.Table && popupData.Table.length > 0 && (
                        <>
                          {renderRecordAsCard(popupData.Table[0])}
                          {popupData.Table.length > 1 && renderTable(popupData.Table, "Import Qty Kgs Records")}
                        </>
                      )}
                      {popupData.Table1 && renderTable(popupData.Table1, "Additional Details")}
                      {popupData.Table2 && renderTable(popupData.Table2, "Related Information")}
                    </>
                  ) : (
                    <>
                      {popupData.Table && popupData.Table.length > 0 && (
                        <>
                          {renderRecordAsCard(popupData.Table[0])}
                          {popupData.Table.length > 1 && renderTable(popupData.Table, "Sales Shipped under MOOWR")}
                        </>
                      )}
                      {popupData.Table1 && renderTable(popupData.Table1, "Additional Details")}
                      {popupData.Table2 && renderTable(popupData.Table2, "Related Information")}
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Table Modal */}
      {fullscreenTable && (
        <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col">
          <div className="bg-white border-b sticky top-0 z-50 px-2 py-1">
            <h2 className="text-lg font-bold">Expanded View</h2>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {fullscreenTable === "table1" && data?.Table && (() => {
              const mainConfig = isColumnConfigTable(data.Table1) ? data.Table1 : null;
              const mainColumns = data.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
              const mainTotals = isTotalsTable(data.Table2, mainColumns) ? data.Table2 : null;
              return renderTable(filterTableData(data.Table), activeTab, "table1", true, mainTotals, mainConfig);
            })()}
            {fullscreenTable === "table2" && data?.Table1 && !isColumnConfigTable(data.Table1) && renderTable(filterTableData(data.Table1), "Additional Details", "table2", true)}
            {fullscreenTable === "table3" && data?.Table2 && !isTotalsTable(data.Table2, data.Table?.length ? getOrderedColumns(data.Table, isColumnConfigTable(data.Table1) ? data.Table1 : null) : []) && renderTable(filterTableData(data.Table2), "Related Records", "table3", true)}
            {fullscreenTable === "table4" && data?.Table3 && renderTable(filterTableData(data.Table3), "More Information", "table4", true)}
            {fullscreenTable === "table5" && data?.Table4 && renderTable(filterTableData(data.Table4), "Extra Data", "table5", true)}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoEData;






